import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  city?: string;
  address?: string;
  zip?: string;
  rpps_number?: string;
  full_name: string;
}

interface User {
  id: number;
  email_address: string;
}

interface ClaimRequest {
  id: number;
  rpps_number: string;
  status: 'pending' | 'approved' | 'rejected';
  justification?: string;
  admin_notes?: string;
  created_at: string;
  physiotherapist: Physiotherapist;
  user: User;
}

interface Props {
  request: ClaimRequest;
}

const getStatusBadge = (status: string) => {
  const statusConfig = {
    pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    approved: { label: 'Approuvé', className: 'bg-green-100 text-green-800 border-green-300' },
    rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800 border-red-300' }
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

  return (
    <Badge className={config.className}>
      {config.label}
    </Badge>
  );
};

export default function ClaimRequestShow({ request }: Props) {
  const [adminNotes, setAdminNotes] = useState(request.admin_notes || '');

  const handleApprove = () => {
    if (confirm(`Approuver la revendication ? La fiche RPPS de ${request.physiotherapist.full_name} sera attribuée à ${request.user.email_address}.`)) {
      router.post(`/admin/claim_requests/${request.id}/approve`, {}, {
        onSuccess: () => {
          router.visit('/admin/claim_requests');
        }
      });
    }
  };

  const handleReject = () => {
    if (confirm(`Rejeter la revendication de ${request.user.email_address} ?`)) {
      router.post(`/admin/claim_requests/${request.id}/reject`, {
        admin_notes: adminNotes
      }, {
        onSuccess: () => {
          router.visit('/admin/claim_requests');
        }
      });
    }
  };

  return (
    <AdminLayout title={`Revendication #${request.id}`}>
      <Head title={`Revendication #${request.id} - Admin`} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link href="/admin/claim_requests">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Revendication de fiche</h1>
              <p className="text-gray-600">#{request.id} - {request.physiotherapist.full_name}</p>
            </div>
          </div>
        </div>

        {/* Status and Actions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3">
                Statut : {getStatusBadge(request.status)}
              </CardTitle>
              {request.status === 'pending' && (
                <div className="flex gap-2">
                  <Button onClick={handleApprove} variant="default" className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approuver
                  </Button>
                  <Button onClick={handleReject} variant="destructive">
                    <XCircle className="h-4 w-4 mr-2" />
                    Rejeter
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Request Info */}
            <div>
              <h3 className="font-semibold mb-2">Informations de la demande</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Demandeur :</strong> {request.user.email_address}</p>
                <p><strong>RPPS revendiqué :</strong> {request.rpps_number}</p>
                <p><strong>Demandé le :</strong> {new Date(request.created_at).toLocaleString('fr-FR')}</p>
                {request.justification && (
                  <div>
                    <strong>Justification du demandeur :</strong>
                    <p className="mt-1 p-2 bg-gray-50 rounded text-gray-700">{request.justification}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Physiotherapist Info */}
            <div>
              <h3 className="font-semibold mb-2">Fiche RPPS ciblée</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Nom :</strong> {request.physiotherapist.full_name}</p>
                <p><strong>RPPS :</strong> {request.physiotherapist.rpps_number}</p>
                {request.physiotherapist.address && (
                  <p><strong>Adresse :</strong> {request.physiotherapist.address}</p>
                )}
                {request.physiotherapist.city && (
                  <p><strong>Ville :</strong> {request.physiotherapist.city} {request.physiotherapist.zip}</p>
                )}
                <Link href={`/physiotherapists/${request.physiotherapist.id}`} className="text-green-600 hover:text-green-700 text-sm">
                  Voir la fiche publique
                </Link>
              </div>
            </div>

            {/* Admin Notes */}
            <div>
              <h3 className="font-semibold mb-2">Notes administrateur</h3>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Ajoutez des notes sur cette demande (vérifications effectuées, etc.)"
                rows={4}
                className="mb-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
